/**
 * "Напомнить ответить" preset menu (follow-ups). Opened via the `remind:open`
 * app event from a message's context menu; picking a preset creates a follow-up
 * (a personal reminder to revisit that message). Mirrors the ForwardModal
 * pattern: a tiny global modal driven by an app event, so the message row stays
 * dumb. Quick presets carry a pre-computed time; "Выбрать время" is a native
 * datetime picker; "Без времени" drops it into the inbox with no reminder.
 */
import { useEffect, useState } from 'react';
import { Modal } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { onAppEvent } from '@/lib/appEvents';
import { presetRemindAt, presetSubLabel, type RemindPresetKey } from '@/lib/remindPresets';
import { ClockIcon, AlarmClockIcon, BookmarkIcon } from '@/components/icons';

const PRESETS: { key: RemindPresetKey; labelKey: string }[] = [
  { key: 'hour', labelKey: 'remind.hour' },
  { key: 'evening', labelKey: 'remind.evening' },
  { key: 'morning', labelKey: 'remind.morning' },
  { key: 'week', labelKey: 'remind.week' },
];

function Row(props: { onClick: () => void; icon: React.ReactNode; label: string; sub?: string }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-default/40"
    >
      <span className="grid h-7 w-7 shrink-0 place-items-center text-muted">{props.icon}</span>
      <span className="min-w-0 flex-1 truncate text-ink">{props.label}</span>
      {props.sub && <span className="shrink-0 text-xs tabular-nums text-muted">{props.sub}</span>}
    </button>
  );
}

export function RemindMenu() {
  const { t } = useTranslation();
  const [messageId, setMessageId] = useState<string | null>(null);
  const open = messageId !== null;

  useEffect(
    () =>
      onAppEvent('remind:open', (d) => {
        setMessageId(d.messageId);
      }),
    [],
  );

  async function create(remindAt: string | null) {
    const id = messageId;
    if (!id) return;
    setMessageId(null);
    try {
      await api.createFollowUp({ messageId: id, remindAt });
      toast.success(t('remind.saved'));
    } catch (err) {
      toast.error(...errToToast(err, t('remind.error')));
    }
  }

  return (
    <Modal.Backdrop isOpen={open} onOpenChange={(o) => !o && setMessageId(null)}>
      <Modal.Container size="sm" placement="top">
        <Modal.Dialog aria-label={t('remind.title')} className="p-2">
          <div className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted">
            {t('remind.title')}
          </div>
          <Row onClick={() => void create(presetRemindAt('hour').toISOString())} icon={<AlarmClockIcon className="h-4 w-4" />} label={t('remind.hour')} sub={presetSubLabel('hour')} />
          {PRESETS.slice(1).map((p) => (
            <Row
              key={p.key}
              onClick={() => void create(presetRemindAt(p.key).toISOString())}
              icon={<ClockIcon className="h-4 w-4" />}
              label={t(p.labelKey)}
              sub={presetSubLabel(p.key)}
            />
          ))}
          <label className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-default/40">
            <span className="grid h-7 w-7 shrink-0 place-items-center text-muted">
              <ClockIcon className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1 text-ink">{t('remind.pick')}</span>
            <input
              type="datetime-local"
              onChange={(e) => {
                // datetime-local commits "YYYY-MM-DDTHH:mm" (≥16 chars) when complete.
                if (e.target.value.length >= 16) void create(new Date(e.target.value).toISOString());
              }}
              className="rounded-md border border-separator bg-default/40 px-2 py-1 text-xs text-ink outline-none focus:border-accent"
            />
          </label>
          <Row onClick={() => void create(null)} icon={<BookmarkIcon className="h-4 w-4" />} label={t('remind.none')} sub={t('remind.noneSub')} />
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
